import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Ticket, 
  CheckCircle, 
  XCircle, 
  Calendar, 
  MapPin, 
  User,
  AlertCircle,
  Loader2,
  QrCode
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function TicketVerify() {
  const { qrCode } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkInResult, setCheckInResult] = useState(null);

  useEffect(() => {
    if (qrCode) {
      verifyTicket();
    }
  }, [qrCode]);

  const verifyTicket = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/tickets/verify/${qrCode}`);
      setTicket(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ticket not found');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to check in tickets');
      navigate('/login');
      return;
    }

    setCheckingIn(true);
    try {
      const response = await axios.post(`${API_URL}/tickets/scan`, {
        qr_code: qrCode,
        event_id: ticket.event_id
      });
      setCheckInResult(response.data);
      setTicket(prev => ({ ...prev, status: 'used' }));
      toast.success('Ticket checked in successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Check-in failed';
      toast.error(errorMsg);
      setCheckInResult({ success: false, message: errorMsg });
    } finally {
      setCheckingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying ticket...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-card border-red-500/30">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-red-400 mb-2">Invalid Ticket</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button variant="outline" onClick={() => navigate('/')}>
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isUsed = ticket?.status === 'used';
  const isActive = ticket?.status === 'active';

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-md mx-auto pt-8">
        {/* Status Header */}
        <div className={`text-center mb-6 p-6 rounded-xl ${
          isUsed ? 'bg-yellow-500/20 border border-yellow-500/30' :
          isActive ? 'bg-green-500/20 border border-green-500/30' :
          'bg-red-500/20 border border-red-500/30'
        }`}>
          {isUsed ? (
            <>
              <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-3" />
              <h1 className="text-2xl font-bold text-yellow-400">Already Used</h1>
              <p className="text-yellow-400/70 text-sm mt-1">This ticket has been checked in</p>
            </>
          ) : isActive ? (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-3" />
              <h1 className="text-2xl font-bold text-green-400">Valid Ticket</h1>
              <p className="text-green-400/70 text-sm mt-1">Ready for check-in</p>
            </>
          ) : (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-3" />
              <h1 className="text-2xl font-bold text-red-400">Invalid</h1>
              <p className="text-red-400/70 text-sm mt-1">This ticket cannot be used</p>
            </>
          )}
        </div>

        {/* Ticket Details */}
        <Card className="bg-card border-white/10 mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/20 rounded-lg">
                <Ticket className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{ticket.event_title}</CardTitle>
                <CardDescription>{ticket.tier_name}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <User className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Ticket Holder:</span>
              <span className="font-medium">{ticket.fan_name}</span>
            </div>
            
            {ticket.event_date && (
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Date:</span>
                <span>{new Date(ticket.event_date).toLocaleDateString()}</span>
              </div>
            )}
            
            {ticket.location && (
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Location:</span>
                <span>{ticket.location}</span>
              </div>
            )}

            <div className="flex items-center gap-3 text-sm">
              <QrCode className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Ticket ID:</span>
              <span className="font-mono text-xs">{ticket.id?.substring(0, 8)}...</span>
            </div>

            <div className="pt-4 border-t border-white/10">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Status</span>
                <Badge className={
                  isUsed ? 'bg-yellow-500/20 text-yellow-400' :
                  isActive ? 'bg-green-500/20 text-green-400' :
                  'bg-red-500/20 text-red-400'
                }>
                  {ticket.status?.toUpperCase()}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Check-in Button (for staff) */}
        {isActive && (
          <Card className="bg-card border-white/10">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground text-center mb-4">
                Staff: Tap below to check in this ticket
              </p>
              <Button 
                className="w-full h-14 text-lg gap-2" 
                onClick={handleCheckIn}
                disabled={checkingIn}
                data-testid="check-in-btn"
              >
                {checkingIn ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Checking In...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Check In Ticket
                  </>
                )}
              </Button>
              {!isAuthenticated && (
                <p className="text-xs text-center text-muted-foreground mt-3">
                  Staff must be logged in to check in tickets
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Check-in Result */}
        {checkInResult && (
          <Card className={`mt-4 ${checkInResult.success !== false ? 'border-green-500/30 bg-green-500/10' : 'border-red-500/30 bg-red-500/10'}`}>
            <CardContent className="pt-6 text-center">
              {checkInResult.success !== false ? (
                <>
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="font-semibold text-green-400">Check-in Successful!</p>
                </>
              ) : (
                <>
                  <XCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
                  <p className="font-semibold text-red-400">{checkInResult.message}</p>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Back Button */}
        <div className="mt-6 text-center">
          <Button variant="ghost" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
